import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import {
  Tags,
  Plus,
  Search,
  Edit2,
  Trash2,
  FolderTree,
  AlertTriangle,
  X
} from 'lucide-react';
import { fetchData } from '../api/api';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import useDebounce from '../utils/debounce';
import PageHeader from '../components/layout/PageHeader';
import GlassmorphicCard from '../components/common/GlassmorphicCard';
import Button from '../components/common/Button';
import InputField from '../components/common/InputField';
import Modal from '../components/common/Modal';
import Table from '../components/common/Table';

const CategoriesPage = () => {
  const { authToken, selectedShopCode } = useContext(AuthContext);
  const { showSuccess, showError } = useToast();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 250);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Delete Modal states
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchCategories = useCallback(async () => {
    if (!authToken || !selectedShopCode) return;
    setLoading(true);
    try {
      const response = await fetchData('api/pos/v1/category/all', 'GET', null, authToken, selectedShopCode);
      const list = response?.data || response?.categories || (Array.isArray(response) ? response : []);
      setCategories(list);
    } catch (err) {
      showError('Failed to fetch categories list.');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [authToken, selectedShopCode, showError]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Filtered categories
  const filteredCategories = useMemo(() => {
    if (!debouncedSearchTerm.trim()) return categories;
    const term = debouncedSearchTerm.toLowerCase();
    return categories.filter(
      (cat) =>
        cat.name?.toLowerCase().includes(term) ||
        cat.description?.toLowerCase().includes(term)
    );
  }, [categories, debouncedSearchTerm]);

  const handleOpenAddModal = () => {
    setEditingCategory(null);
    setFormData({ name: '', description: '' });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name || '',
      description: category.description || '',
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCategory(null);
    setFormData({ name: '', description: '' });
    setFormErrors({});
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name?.trim()) {
      errors.name = 'Category name is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description?.trim() || '',
      };

      if (editingCategory?.id) {
        // Update category
        await fetchData(
          `api/pos/v1/category/update/${editingCategory.id}`,
          'POST',
          payload,
          authToken,
          selectedShopCode
        );
        showSuccess('Category updated successfully.');
      } else {
        // Create category
        await fetchData(
          'api/pos/v1/category/create',
          'POST',
          payload,
          authToken,
          selectedShopCode
        );
        showSuccess('Category created successfully.');
      }

      handleCloseModal();
      fetchCategories();
    } catch (err) {
      showError(err.message || 'Failed to save category.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenDeleteModal = (category) => {
    setCategoryToDelete(category);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!categoryToDelete?.id) return;
    setIsDeleting(true);
    try {
      await fetchData(
        `api/pos/v1/category/delete/${categoryToDelete.id}`,
        'POST',
        null,
        authToken,
        selectedShopCode
      );
      showSuccess(`Category "${categoryToDelete.name}" deleted successfully.`);
      setIsDeleteModalOpen(false);
      setCategoryToDelete(null);
      fetchCategories();
    } catch (err) {
      showError(err.message || 'Failed to delete category.');
    } finally {
      setIsDeleting(false);
    }
  };

  const tableHeaders = ['Category Name', 'Description', 'Actions'];

  const renderRow = (category, idx) => (
    <tr
      key={category.id || idx}
      className="hover:bg-slate-50/70 dark:hover:bg-slate-800/40 transition-colors"
    >
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200/60 dark:border-emerald-800/50 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0">
            <Tags className="w-4 h-4" />
          </div>
          <div>
            <span className="font-medium text-slate-900 dark:text-white">
              {category.name}
            </span>
          </div>
        </div>
      </td>
      <td className="px-6 py-4">
        <p className="text-sm text-slate-600 dark:text-slate-300 line-clamp-2 max-w-md">
          {category.description || (
            <span className="text-slate-400 dark:text-slate-500 italic">No description provided</span>
          )}
        </p>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right">
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="light"
            onClick={() => handleOpenEditModal(category)}
            className="text-slate-600 dark:text-slate-300 hover:text-emerald-600 dark:hover:text-emerald-400"
            title="Edit category"
          >
            <Edit2 className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Edit</span>
          </Button>
          <Button
            size="sm"
            variant="danger"
            onClick={() => handleOpenDeleteModal(category)}
            className="hover:bg-rose-700"
            title="Delete category"
          >
            <Trash2 className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Delete</span>
          </Button>
        </div>
      </td>
    </tr>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Categories"
        subtitle="Manage catalog categories to organize your product inventory"
        breadcrumbs={[{ label: 'Categories' }]}
        action={
          <Button
            variant="primary"
            icon={<Plus className="w-4 h-4" />}
            onClick={handleOpenAddModal}
          >
            Add Category
          </Button>
        }
      />

      {/* Filter and Search Bar */}
      <GlassmorphicCard className="p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search categories by name or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-9 py-2 rounded-lg text-sm bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 transition-colors"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 self-end sm:self-center">
            <FolderTree className="w-4 h-4 text-emerald-500" />
            <span>
              Total Categories:{' '}
              <strong className="text-slate-800 dark:text-slate-200 font-semibold">
                {filteredCategories.length}
              </strong>
            </span>
          </div>
        </div>
      </GlassmorphicCard>

      {/* Categories Table */}
      <Table
        headers={tableHeaders}
        data={filteredCategories}
        renderRow={renderRow}
        loading={loading}
        emptyMessage={
          debouncedSearchTerm
            ? `No categories matching "${debouncedSearchTerm}"`
            : 'No categories found. Click "Add Category" to create your first category.'
        }
      />

      {/* Add / Edit Category Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingCategory ? 'Edit Category' : 'Add New Category'}
        size="default"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <InputField
            label="Category Name"
            name="name"
            placeholder="e.g. Beverages, Antibiotics, Dairy"
            value={formData.name}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, name: e.target.value }));
              if (formErrors.name) {
                setFormErrors((prev) => ({ ...prev, name: '' }));
              }
            }}
            required
            error={formErrors.name}
            icon={<Tags className="w-4 h-4" />}
          />

          <div className="mb-4">
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400 mb-1.5">
              Description (Optional)
            </label>
            <textarea
              name="description"
              rows={3}
              placeholder="Brief description of products grouped under this category"
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              className="w-full px-3.5 py-2.5 rounded-lg text-sm bg-slate-50/70 dark:bg-slate-900/60 text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-700 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:bg-white dark:focus:bg-slate-900 focus:border-emerald-500 dark:focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all duration-200 ease-in-out outline-none resize-none"
            />
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-700/80">
            <Button
              type="button"
              variant="light"
              onClick={handleCloseModal}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? editingCategory
                  ? 'Saving...'
                  : 'Creating...'
                : editingCategory
                ? 'Update Category'
                : 'Create Category'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => !isDeleting && setIsDeleteModalOpen(false)}
        title="Delete Category"
        size="small"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3.5 p-3.5 rounded-xl bg-rose-50/70 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900/50">
            <div className="p-2 rounded-lg bg-rose-100 dark:bg-rose-900/50 text-rose-600 dark:text-rose-400 shrink-0">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-rose-900 dark:text-rose-200">
                Are you sure you want to delete this category?
              </h4>
              <p className="text-xs text-rose-700 dark:text-rose-300 mt-1">
                You are about to delete{' '}
                <strong className="font-semibold text-rose-950 dark:text-rose-100">
                  {categoryToDelete?.name}
                </strong>
                . This action cannot be undone.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-700/80">
            <Button
              type="button"
              variant="light"
              onClick={() => setIsDeleteModalOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete Category'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CategoriesPage;
